import { createRouter, createWebHistory } from 'vue-router';
import mixpanelSingleton from '@/mixpanel/mixpanelSingleton.js';
import store from '../vuex/store.js';
// Layouts
import AccountLayout from '../components/layouts/AccountLayout.vue';
import WorkspaceLayout from '../components/layouts/WorkspaceLayout.vue';

// Account Pages
import AccountProfile from '../pages/account/Profile.vue';
import AccountNotifications from '../pages/account/Notifications.vue';

// Workspace Pages
import WorkspaceProjects from '../pages/workspace/Projects.vue';
import WorkspaceMembers from '../pages/workspace/Members.vue';
import WorkspaceExternalUsers from '../pages/workspace/ExternalUsers.vue';
import WorkspaceSettings from '../pages/workspace/Settings.vue';
import WorkspaceBilling from '../pages/workspace/Billing.vue';
import WorkspaceRoles from '../pages/workspace/Roles.vue';
import WorkspaceRole from '../pages/workspace/Role.vue';
import WorkspaceInvitations from '../pages/workspace/Invitations.vue';
import WorkspaceApi from '../pages/workspace/API.vue';

// Invitations
import Invitations from '../pages/Invitations.vue';

// Legacy pages
import ProjectPage from '../components/all/ProjectPage.vue';
import PrototypePage from '../components/prototype/PrototypePage.vue';
import ProjectError from '../components/error-pages/project-error.vue';

const router = createRouter({
  history: createWebHistory('/app/'), // Specify your base path here
  routes: [
    {
      path: '/account',
      component: AccountLayout,
      name: 'account',
      meta: { requiresLogin: true },
      redirect: '/account/profile',
      children: [
        {
          path: 'profile',
          name: 'accountProfile',
          component: AccountProfile,
          props: true,
        },
        {
          path: 'billing',
          name: 'accountBilling',
          redirect: '/account/profile',
        },
        {
          path: 'notifications',
          name: 'accountNotifications',
          component: AccountNotifications,
          props: true,
        },
        {
          path: 'plan',
          name: 'accountPlan',
          redirect: '/account/profile',
        },
      ],
    },
    {
      path: '/workspace/:workspaceHash?',
      component: WorkspaceLayout,
      meta: { requiresLogin: true, requiresWorkspace: true },
      name: 'workspace',
      beforeEnter: to => {
        const name = to.name === 'workspace' ? 'workspace-projects' : to.name;
        const workspaceHash =
          to.params.workspaceHash || store.getters['workspace/getHash'];
        const params = {
          ...to.params,
          workspaceHash,
        };

        if (
          to.name !== name ||
          to.params.workspaceHash !== params.workspaceHash
        ) {
          return {
            name,
            params,
          };
        }
        return true;
      },
      children: [
        {
          path: 'projects',
          name: 'workspace-projects',
          component: WorkspaceProjects,
          props: true,
        },
        {
          path: 'people',
          name: 'workspace-people',
          redirect: to => ({ name: 'workspace-members', params: to.params }),
          children: [
            {
              path: '',
              name: 'workspace-members',
              component: WorkspaceMembers,
              props: true,
              exact: true,
            },
            {
              path: 'external',
              name: 'workspace-external-users',
              component: WorkspaceExternalUsers,
              props: true,
              exact: true,
            },
            {
              path: 'invitations',
              name: 'workspace-invitations',
              component: WorkspaceInvitations,
              props: true,
              exact: true,
            },
          ],
        },
        {
          path: 'settings',
          name: 'workspace-settings',
          component: WorkspaceSettings,
          props: true,
        },
        {
          path: 'billing',
          name: 'workspace-billing',
          component: WorkspaceBilling,
          props: true,
        },
        {
          path: 'roles',
          name: 'workspace-roles',
          component: WorkspaceRoles,
          props: { workspaceId: true },
          children: [
            {
              path: ':role',
              name: 'workspace-role',
              component: WorkspaceRole,
              props: true,
            },
          ],
        },
        {
          path: 'api',
          name: 'workspace-api',
          component: WorkspaceApi,
          props: true,
        },
      ],
    },
    {
      path: '/invitations',
      component: Invitations,
      name: 'invitations',
      meta: { requiresLogin: true },
    },
    {
      path: '/project',
      meta: { requiresWorkspace: true, requiresLogin: true },
      redirect: () => {
        const hash = store.getters['user/projects/getOpen'];

        if (!hash) {
          return { name: 'workspace' };
        }

        return `/project/${hash}`;
      },
      name: 'defaultProject',
      children: [
        {
          path: ':hash',
          meta: { requiresLogin: false },
          component: ProjectPage,
          name: 'project',
        },
        {
          path: ':hash/play',
          meta: { requiresLogin: false },
          component: PrototypePage,
          name: 'prototype',
        },
      ],
    },
    {
      path: '/',
      meta: { requiresLogin: true },
      redirect: to => {
        const isLoggedIn = store.getters['user/account/getIsLoggedIn'];
        // Redirect old hash URLs to new history ones
        if (to.hash !== '') {
          const fullPath = to.hash.substring(1);
          if (fullPath.includes('?')) {
            const url = fullPath.split('?');
            const path = url[0];
            const queriesObj = {};
            if (path.startsWith('/project') || path.startsWith('project')) {
              const queries = url[1].split('&');
              queries.forEach(query => {
                const [q, val] = query.split('=');
                queriesObj[q] = val;
              });
            }
            return { path, hash: '', query: queriesObj };
          }
          return { path: fullPath, hash: '' };
        }
        if (!isLoggedIn) {
          window.location.href = '/login';
        }
        return { name: 'workspace' };
      },
      name: 'home',
    },
    {
      path: '/project-error/404',
      component: ProjectError,
      name: 'project-error404',
      props: {
        title: 'Oops, this project does not exist!',
        description:
          'It might have been abducted by aliens... or simply deleted.',
        icon: 'app-icons/no-project',
      },
    },
    {
      path: '/project-error/403',
      component: ProjectError,
      name: 'project-error403',
      props: {
        title: 'Hmm, this project is not accessible.',
        description: 'Perhaps you should try asking the owner more kindly.',
        icon: 'app-icons/no-project',
      },
    },
    {
      path: '/error/404',
      component: ProjectError,
      name: 'page404',
      props: {
        title: "Ouch, we can't seem to find this page!",
        description:
          'Must have been eaten by the notorious page-eating monster! Or was it a typo?',
        icon: 'app-icons/no-page',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      meta: { requiresLogin: true },
      redirect: () => {
        const isLoggedIn = store.getters['user/account/getIsLoggedIn'];
        if (!isLoggedIn) {
          window.location.href = '/login';
        }
        return '/error/404';
      },
    },
  ],
});

router.beforeEach(async to => {
  const isLoggedIn = store.getters['user/account/getIsLoggedIn'];
  const requiresLogin = to.matched.reduce((acc, current) => {
    if (current.meta.requiresLogin === true) return true;
    if (current.meta.requiresLogin === false) return false;
    return acc;
  });
  if (requiresLogin && !isLoggedIn) {
    window.location.href = '/login';
    return false;
  }
  let workspaceHash = '';
  if (to.matched.some(record => record.meta.requiresWorkspace)) {
    if (
      !store.getters['workspace/getHash'] || // No workspace loaded
      (to.params.workspaceHash &&
        store.getters['workspace/getHash'] !== to.params.workspaceHash) || // Different workspace loaded than url
      (to.params.hash &&
        store.getters['project/getWorkspaceId'] !==
          store.getters['workspace/getId']) || // Project loaded workspace is different than current workspace
      to.query.refreshWorkspace
    ) {
      workspaceHash = to.params.workspaceHash ? to.params.workspaceHash : null;
      let projectHash = to.params.hash ? to.params.hash : null;

      if (to.query.refreshWorkspace) {
        to.query.refreshWorkspace = undefined;
        workspaceHash = null;
        projectHash = null;
      }

      await store.dispatch('loadWorkspace', {
        workspaceHash,
        projectHash,
        isLoggedIn,
      });
    }
  }

  workspaceHash = store.getters['workspace/getHash'];

  if (to.matched[0].name === 'workspace') {
    if (to.params.workspaceHash !== workspaceHash) {
      return { name: to.name, params: { workspaceHash }, query: to.query };
    }
    if (
      !store.getters['user/memberships/isInCurrentWorkspace'] &&
      !store.getters['user/externalMemberships/isInCurrentWorkspace']
    ) {
      workspaceHash = store.getters['user/memberships/getFirstWorkspaceHash'];
      return { name: to.name, params: { workspaceHash }, query: to.query };
    }
  }

  if (to.name === 'project' && isLoggedIn && !to.params.hash) {
    const hash = store.getters['user/projects/getOpen'];
    // If there is an open project, redirect to it
    if (hash !== null) {
      return true;
    }

    // If there is no open project, redirect to workspace
    return { name: 'workspace', params: { workspaceHash } };
  }

  // Mix panel event
  mixpanelPageViewed(to, isLoggedIn);

  return true;
});

export default router;

function mixpanelPageViewed(to, isLoggedIn) {
  if (process.env.MIX_ANALYTICS_ENABLED === 'true') {
    const accountPages = [
      'accountProfile',
      'accountBilling',
      'accountNotifications',
      'accountPlan',
      'list',
    ];
    const workspacePages = [
      'workspace-projects',
      'workspace-people',
      'workspace-roles',
      'workspace-settings',
      'workspace-billing',
      'workspace-api',
    ];

    if (accountPages.includes(to.name) && isLoggedIn) {
      mixpanelSingleton.track('page_viewed', {
        path: to.name,
      });
    } else if (workspacePages.includes(to.name) && isLoggedIn) {
      const workspaceId = store.getters['workspace/getId'];

      mixpanelSingleton.track('page_viewed', {
        path: to.name,
        workspace_id: workspaceId,
      });
    }
  }
}
